/* eslint-disable max-lines */
import { useAtomValue } from "jotai";
import type Konva from "konva";
import type { KonvaEventObject } from "konva/lib/Node";
import { useCallback, useEffect, useRef, useState } from "react";
import { Circle, Group, Image, Rect } from "react-konva";

import {
  DEFAULT_TOLERANCE,
  floodFillEraseWithDiff,
  floodFillMask,
  performBrushErase,
  performManualBrushErase
} from "@design-lab/features/design-artwork/lib/cutoutPixelOps";
import { trackImageEditToolUsed } from "@design-lab/shared/lib/analytics";
import { useShowManualBgRemovalBrushSize } from "@design-lab/shared/lib/config";
import {
  brushSizeSliderActiveAtom,
  brushTypeAtom,
  eraseBrushSizeAtom,
  type InteractionMode,
  useRefineHistoryContext
} from "@design-lab/entities/artwork";
import { usePlacementZoomAndPan } from "../hooks/usePlacementZoomAndPan";

type Dimensions = { x: number; y: number; width: number; height: number };

type ImageRefLike = { current: Konva.Image | null };

type EraseOverlayProps = {
  imageRef: ImageRefLike;
  displayDimensions: Dimensions;
  activeTool?: string | undefined;
  artworkId?: string;
  interactionMode?: InteractionMode;
  isSpaceHeld?: boolean;
};

const QUICK_SELECTION_BRUSH_SIZE = 40;

// eslint-disable-next-line max-lines-per-function
export function EraseOverlay({
  imageRef,
  displayDimensions,
  activeTool,
  artworkId,
  interactionMode = "cursor",
  isSpaceHeld = false
}: EraseOverlayProps) {
  const { currentImageData, pushOperation } = useRefineHistoryContext();
  const brushSize = useAtomValue(eraseBrushSizeAtom);
  const brushType = useAtomValue(brushTypeAtom);
  const { zoom } = usePlacementZoomAndPan();
  const isSliderActive = useAtomValue(brushSizeSliderActiveAtom);

  const shouldShowBrushSize = useShowManualBgRemovalBrushSize();

  // Helper function to track tool usage
  const trackToolUsage = useCallback(() => {
    if (!artworkId) return;

    const toolName =
      brushType === "quick-selection" || !shouldShowBrushSize ? "auto_eraser" : "manual_eraser";
    const toolConfig = toolName === "manual_eraser" ? { brush_size: brushSize } : {};
    trackImageEditToolUsed(artworkId, toolName, toolConfig);
  }, [artworkId, brushType, shouldShowBrushSize, brushSize]);

  // Track drawing state for continuous drag erasing
  const [isDrawing, setIsDrawing] = useState(false);
  const dragStartImageDataRef = useRef<string | null>(null);
  const hasDragChangesRef = useRef(false);
  const dragEraseRafRef = useRef<number | null>(null);
  const pendingErasePointerRef = useRef<{ x: number; y: number } | null>(null);

  // Track the last successfully erased stage-space point for stroke interpolation
  const lastErasedStagePointRef = useRef<{ x: number; y: number } | null>(null);

  // Track single-touch start to prevent erase during two-finger gesture initiation
  const singleTouchTimeout = useRef<number | null>(null);
  const pendingTouchEvent = useRef<
    KonvaEventObject<TouchEvent> | KonvaEventObject<MouseEvent> | null
  >(null);

  // Determine if erasing is allowed
  const canErase = interactionMode === "cursor" && !isSpaceHeld;

  // Adjust brush size for zoom to maintain consistent visual size
  const zoomAdjustedBrushSize =
    (brushType === "quick-selection" ? QUICK_SELECTION_BRUSH_SIZE : brushSize) / zoom;

  const workingCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const previewImageRef = useRef<Konva.Image | null>(null);
  const lastPreviewPointRef = useRef<{ x: number; y: number } | null>(null);
  const pendingPointerRef = useRef<{ x: number; y: number } | null>(null);
  const previewRafIdRef = useRef<number | null>(null);
  const ringRef = useRef<Konva.Circle>(null);
  const [ringPosition, setRingPosition] = useState<{ x: number; y: number } | null>(null);
  const lastKnownRingPositionRef = useRef<{ x: number; y: number } | null>(null);

  // Keep a working canvas in sync with the current image data
  useEffect(() => {
    const src = currentImageData;
    if (!src) return;
    const img = new window.Image();
    img.onload = () => {
      const canvas = workingCanvasRef.current ?? document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      workingCanvasRef.current = canvas;
    };
    img.src = src;
  }, [currentImageData]);

  const ensurePreviewCanvas = useCallback(() => {
    const working = workingCanvasRef.current;
    if (!working) return null;
    const full = previewCanvasRef.current ?? document.createElement("canvas");
    if (full.width !== working.width || full.height !== working.height) {
      full.width = working.width;
      full.height = working.height;
    }
    previewCanvasRef.current = full;

    if (previewImageRef.current) {
      previewImageRef.current.image(full);
      previewImageRef.current.getLayer()?.batchDraw();
    }
    return full;
  }, [imageRef]);

  const clearPreview = useCallback(() => {
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    previewImageRef.current?.getLayer()?.batchDraw();
  }, []);

  // Clear preview when tool deactivates or when erasing is disabled
  useEffect(() => {
    if (activeTool !== "erase" || !canErase) {
      lastPreviewPointRef.current = null;
      clearPreview();
      setRingPosition(null);
    } else if (shouldShowBrushSize && brushType === "manual") {
      // Also clear any preview when manual brush is active
      lastPreviewPointRef.current = null;
      clearPreview();
    }
  }, [activeTool, canErase, clearPreview, shouldShowBrushSize, brushType]);

  const schedulePreviewCompute = useCallback(() => {
    if (previewRafIdRef.current != null) return;
    previewRafIdRef.current = window.requestAnimationFrame(() => {
      previewRafIdRef.current = null;
      // Hide preview overlay when dynamic brush is active
      if (shouldShowBrushSize && brushType === "manual") {
        clearPreview();
        return;
      }
      const working = workingCanvasRef.current;
      if (!working) return;
      const full = ensurePreviewCanvas();
      if (!full) return;
      const overlayFullCtx = full.getContext("2d", { willReadFrequently: true });
      const baseCtx = working.getContext("2d", { willReadFrequently: true });
      if (!baseCtx || !overlayFullCtx) return;

      const pointer = pendingPointerRef.current;
      if (!pointer) return;
      const { x, y } = pointer;

      if (x < 0 || y < 0 || x >= working.width || y >= working.height) {
        clearPreview();
        return;
      }

      const imgData = baseCtx.getImageData(0, 0, working.width, working.height);
      const {
        mask,
        width: w,
        height: h,
        count
      } = floodFillMask(imgData, {
        startX: x,
        startY: y,
        tolerance: DEFAULT_TOLERANCE
      });

      overlayFullCtx.clearRect(0, 0, full.width, full.height);
      if (count > 0) {
        const overlayImage = overlayFullCtx.createImageData(w, h);
        const d = overlayImage.data;
        const r = 30;
        const g = 144;
        const b = 255;
        const a = 110;
        for (let i = 0; i < mask.length; i += 1) {
          if (mask[i]) {
            const idx = i * 4;
            d[idx] = r;
            d[idx + 1] = g;
            d[idx + 2] = b;
            d[idx + 3] = a;
          }
        }
        overlayFullCtx.putImageData(overlayImage, 0, 0);
      }
      // Re-attach to ensure Konva marks node dirty for redraw
      if (previewImageRef.current) {
        previewImageRef.current.image(full);
      }
      previewImageRef.current?.getLayer()?.batchDraw();
    });
  }, [clearPreview, ensurePreviewCanvas, shouldShowBrushSize, brushType]);

  const handlePreviewMouseMove = useCallback(
    (_: KonvaEventObject<MouseEvent>) => {
      if (activeTool !== "erase") return;
      if (!imageRef.current) return;
      const pointer = imageRef.current.getStage()?.getPointerPosition();
      if (!pointer) return;
      const absTransform = imageRef.current.getAbsoluteTransform().copy();
      absTransform.invert();
      const local = absTransform.point(pointer);

      // Update ring position to follow mouse in local coordinates
      const newPosition = { x: local.x, y: local.y };
      setRingPosition(newPosition);
      lastKnownRingPositionRef.current = newPosition;

      const working = workingCanvasRef.current;
      if (!working) return;
      const node = imageRef.current;
      const ratioX = working.width / Math.max(node.width(), 1);
      const ratioY = working.height / Math.max(node.height(), 1);
      const x = Math.floor(local.x * ratioX);
      const y = Math.floor(local.y * ratioY);
      const last = lastPreviewPointRef.current;
      if (last && last.x === x && last.y === y) return;
      lastPreviewPointRef.current = { x, y };
      pendingPointerRef.current = { x, y };
      schedulePreviewCompute();
    },
    [activeTool, imageRef, schedulePreviewCompute]
  );

  useEffect(
    () => () => {
      if (previewRafIdRef.current != null) {
        cancelAnimationFrame(previewRafIdRef.current);
        previewRafIdRef.current = null;
      }
    },
    []
  );

  const performEraseAtPoint = useCallback(
    (pointer: { x: number; y: number }, shouldPushToHistory: boolean) => {
      if (!imageRef.current) return false;

      const absTransform = imageRef.current.getAbsoluteTransform().copy();
      absTransform.invert();
      const local = absTransform.point(pointer);

      const canvas = workingCanvasRef.current;
      if (!canvas) return false;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return false;

      const displayWidth = imageRef.current.width();
      const displayHeight = imageRef.current.height();
      const ratioX = canvas.width / Math.max(displayWidth, 1);
      const ratioY = canvas.height / Math.max(displayHeight, 1);

      const x = Math.floor(local.x * ratioX);
      const y = Math.floor(local.y * ratioY);

      if (x < 0 || y < 0 || x >= canvas.width || y >= canvas.height) return false;

      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let totalChanged = 0;
      if (brushType === "quick-selection" || !shouldShowBrushSize) {
        const changes = floodFillEraseWithDiff(imgData, {
          startX: x,
          startY: y,
          tolerance: DEFAULT_TOLERANCE
        });
        totalChanged += changes.size;
      } else {
        const radiusLocal = Math.max(1, zoomAdjustedBrushSize / 2);
        const radiusX = Math.max(1, Math.floor(radiusLocal * ratioX));
        const radiusY = Math.max(1, Math.floor(radiusLocal * ratioY));

        if (brushType === "manual") {
          totalChanged = performManualBrushErase({
            imgData,
            centerX: x,
            centerY: y,
            radiusX,
            radiusY,
            tolerance: DEFAULT_TOLERANCE
          });
        } else {
          totalChanged = performBrushErase({
            imgData,
            centerX: x,
            centerY: y,
            radiusX,
            radiusY,
            tolerance: DEFAULT_TOLERANCE
          });
        }
      }
      if (totalChanged === 0) return false;

      ctx.putImageData(imgData, 0, 0);
      hasDragChangesRef.current = true;

      if (shouldPushToHistory) {
        const dataUrl = canvas.toDataURL();
        pushOperation({
          type: "erase",
          imageData: dataUrl,
          metadata: {
            dimensions: displayDimensions
          },
          timestamp: Date.now()
        });

        trackToolUsage();
      }

      if (imageRef.current) {
        imageRef.current.image(canvas);
        imageRef.current.getLayer()?.batchDraw();
      }

      clearPreview();
      return true;
    },
    [
      imageRef,
      shouldShowBrushSize,
      zoomAdjustedBrushSize,
      brushType,
      pushOperation,
      displayDimensions,
      clearPreview,
      trackToolUsage
    ]
  );

  // Erase a continuous stroke from fromStagePoint to toStagePoint for manual brush.
  // Interpolates intermediate points so dragging produces a solid line rather than discrete dots.
  // Uses a single getImageData/putImageData round-trip and updates Konva directly (no toDataURL).
  const performEraseStroke = useCallback(
    (fromStagePoint: { x: number; y: number } | null, toStagePoint: { x: number; y: number }) => {
      if (!imageRef.current) return false;

      const canvas = workingCanvasRef.current;
      if (!canvas) return false;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return false;

      const absTransform = imageRef.current.getAbsoluteTransform().copy();
      absTransform.invert();

      const displayWidth = imageRef.current.width();
      const displayHeight = imageRef.current.height();
      const ratioX = canvas.width / Math.max(displayWidth, 1);
      const ratioY = canvas.height / Math.max(displayHeight, 1);

      const toLocal = absTransform.point(toStagePoint);
      const toX = Math.floor(toLocal.x * ratioX);
      const toY = Math.floor(toLocal.y * ratioY);

      if (toX < 0 || toY < 0 || toX >= canvas.width || toY >= canvas.height) return false;

      const radiusLocal = Math.max(1, zoomAdjustedBrushSize / 2);
      const radiusX = Math.max(1, Math.floor(radiusLocal * ratioX));
      const radiusY = Math.max(1, Math.floor(radiusLocal * ratioY));

      let fromX = toX;
      let fromY = toY;
      if (fromStagePoint) {
        const fromLocal = absTransform.point(fromStagePoint);
        fromX = Math.floor(fromLocal.x * ratioX);
        fromY = Math.floor(fromLocal.y * ratioY);
      }

      const dx = toX - fromX;
      const dy = toY - fromY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const stepSize = Math.max(1, Math.min(radiusX, radiusY) / 2);
      const steps = Math.max(1, Math.ceil(distance / stepSize));

      // Single read of the full ImageData; all interpolated circles write into it
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let totalChanged = 0;

      // When continuing from a previous point, start at i=1 to avoid re-erasing the last point
      const startI = fromStagePoint ? 1 : 0;
      for (let i = startI; i <= steps; i += 1) {
        const t = i / steps;
        const x = Math.round(fromX + dx * t);
        const y = Math.round(fromY + dy * t);
        if (x >= 0 && y >= 0 && x < canvas.width && y < canvas.height) {
          totalChanged += performManualBrushErase({
            imgData,
            centerX: x,
            centerY: y,
            radiusX,
            radiusY,
            tolerance: DEFAULT_TOLERANCE
          });
        }
      }

      if (totalChanged === 0) return false;

      ctx.putImageData(imgData, 0, 0);
      hasDragChangesRef.current = true;

      // Update Konva directly — no toDataURL or async Image needed during drag
      imageRef.current.image(canvas);
      imageRef.current.getLayer()?.batchDraw();

      clearPreview();
      return true;
    },
    [imageRef, zoomAdjustedBrushSize, clearPreview]
  );

  // Handle mouse/touch down for manual brush drag erasing
  const handleMouseDown = useCallback(
    (e: KonvaEventObject<MouseEvent> | KonvaEventObject<TouchEvent>) => {
      if (activeTool !== "erase") return;
      if (!canErase) return;
      if (!imageRef.current) return;

      const evt = e.evt as TouchEvent;

      // Check if this is a multi-touch gesture (pinch-to-zoom)
      if (evt.touches && evt.touches.length > 1) {
        // Multi-touch detected, clear any pending single-touch
        if (singleTouchTimeout.current !== null) {
          clearTimeout(singleTouchTimeout.current);
          singleTouchTimeout.current = null;
          pendingTouchEvent.current = null;
        }
        return;
      }

      // For single-touch events, wait briefly to see if a second finger comes
      if (evt.touches && evt.touches.length === 1) {
        // Clear any existing timeout
        if (singleTouchTimeout.current !== null) {
          clearTimeout(singleTouchTimeout.current);
        }

        // Store the event
        pendingTouchEvent.current = e;

        // Wait 50ms before actually starting the erase
        singleTouchTimeout.current = window.setTimeout(() => {
          // Check if we should still proceed with erase
          if (pendingTouchEvent.current && activeTool === "erase" && canErase && imageRef.current) {
            const stage = imageRef.current.getStage();
            const pointer = stage?.getPointerPosition();
            if (pointer) {
              if (brushType === "manual" && shouldShowBrushSize) {
                dragStartImageDataRef.current = currentImageData || null;
                hasDragChangesRef.current = false;
                setIsDrawing(true);
                lastErasedStagePointRef.current = pointer;
                performEraseStroke(null, pointer);
              } else if (brushType === "quick-selection" || !shouldShowBrushSize) {
                performEraseAtPoint(pointer, true);
              }
            }
          }
          singleTouchTimeout.current = null;
          pendingTouchEvent.current = null;
        }, 50);

        return;
      }

      // Mouse event (not touch) - proceed immediately
      const stage = imageRef.current.getStage();
      const pointer = stage?.getPointerPosition();
      if (!pointer) return;

      if (brushType === "manual" && shouldShowBrushSize) {
        dragStartImageDataRef.current = currentImageData || null;
        hasDragChangesRef.current = false;
        setIsDrawing(true);
        lastErasedStagePointRef.current = pointer;
        performEraseStroke(null, pointer);
      } else if (brushType === "quick-selection" || !shouldShowBrushSize) {
        performEraseAtPoint(pointer, true);
      }
    },
    [
      activeTool,
      canErase,
      brushType,
      shouldShowBrushSize,
      imageRef,
      currentImageData,
      performEraseAtPoint,
      performEraseStroke
    ]
  );

  // Handle mouse/touch move for continuous erasing (throttled)
  const handleDragErase = useCallback(
    (e: KonvaEventObject<MouseEvent> | KonvaEventObject<TouchEvent>) => {
      // If we have a pending single-touch, don't start dragging yet
      if (singleTouchTimeout.current !== null) {
        return;
      }

      if (activeTool !== "erase") return;
      if (!canErase) return;
      if (!isDrawing) return;
      if (!imageRef.current) return;

      // Check if this is a multi-touch gesture (pinch-to-zoom)
      const evt = e.evt as TouchEvent;
      if (evt.touches && evt.touches.length > 1) {
        // Multi-touch detected, cancel any pending touch and stop erasing
        if (singleTouchTimeout.current !== null) {
          clearTimeout(singleTouchTimeout.current);
          singleTouchTimeout.current = null;
          pendingTouchEvent.current = null;
        }
        setIsDrawing(false);
        return;
      }

      const stage = imageRef.current.getStage();
      const pointer = stage?.getPointerPosition();
      if (!pointer) return;

      if (brushType === "manual" && shouldShowBrushSize) {
        pendingErasePointerRef.current = pointer;

        if (dragEraseRafRef.current !== null) return;

        dragEraseRafRef.current = window.requestAnimationFrame(() => {
          dragEraseRafRef.current = null;
          const pendingPointer = pendingErasePointerRef.current;
          if (pendingPointer) {
            const fromPoint = lastErasedStagePointRef.current;
            performEraseStroke(fromPoint, pendingPointer);
            lastErasedStagePointRef.current = pendingPointer;
          }
        });
      }
    },
    [activeTool, canErase, isDrawing, brushType, shouldShowBrushSize, imageRef, performEraseStroke]
  );

  // Handle mouse/touch up to stop drawing and commit changes
  const handleMouseUp = useCallback(() => {
    // Cancel any pending single-touch timeout
    if (singleTouchTimeout.current !== null) {
      clearTimeout(singleTouchTimeout.current);
      singleTouchTimeout.current = null;
      pendingTouchEvent.current = null;
    }

    if (dragEraseRafRef.current !== null) {
      cancelAnimationFrame(dragEraseRafRef.current);
      dragEraseRafRef.current = null;
    }

    if (isDrawing && hasDragChangesRef.current) {
      const canvas = workingCanvasRef.current;
      if (canvas) {
        const dataUrl = canvas.toDataURL();
        pushOperation({
          type: "erase",
          imageData: dataUrl,
          metadata: {
            dimensions: displayDimensions
          },
          timestamp: Date.now()
        });

        trackToolUsage();
      }
    }

    setIsDrawing(false);
    dragStartImageDataRef.current = null;
    hasDragChangesRef.current = false;
    pendingErasePointerRef.current = null;
    lastErasedStagePointRef.current = null;
  }, [isDrawing, pushOperation, displayDimensions, trackToolUsage]);

  // Reset drawing state when tool changes or erase is disabled
  useEffect(() => {
    if (activeTool !== "erase" || !canErase) {
      if (dragEraseRafRef.current !== null) {
        cancelAnimationFrame(dragEraseRafRef.current);
        dragEraseRafRef.current = null;
      }
      setIsDrawing(false);
      dragStartImageDataRef.current = null;
      hasDragChangesRef.current = false;
      pendingErasePointerRef.current = null;
      lastErasedStagePointRef.current = null;
    }
  }, [activeTool, canErase]);

  useEffect(
    () => () => {
      if (dragEraseRafRef.current !== null) {
        cancelAnimationFrame(dragEraseRafRef.current);
      }
    },
    []
  );

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (singleTouchTimeout.current !== null) {
        clearTimeout(singleTouchTimeout.current);
      }
    },
    []
  );

  // Add global mouse/touch up listeners when drawing to handle mouse up outside image bounds
  useEffect(() => {
    if (!isDrawing) return;

    const handleGlobalMouseUp = () => {
      handleMouseUp();
    };

    window.addEventListener("mouseup", handleGlobalMouseUp);
    window.addEventListener("touchend", handleGlobalMouseUp);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener("mouseup", handleGlobalMouseUp);
      window.removeEventListener("touchend", handleGlobalMouseUp);
    };
  }, [isDrawing, handleMouseUp]);

  if (!displayDimensions) return null;

  // Determine if ring should be shown and where - only show when erasing is allowed
  const shouldShowRing =
    activeTool === "erase" && canErase && (ringPosition || (isSliderActive && shouldShowBrushSize));
  const displayRingPosition =
    ringPosition ||
    (isSliderActive && shouldShowBrushSize
      ? lastKnownRingPositionRef.current || {
          x: displayDimensions.width / 2,
          y: displayDimensions.height / 2
        }
      : null);

  // Compute radii in local space so ring edges align regardless of zoom
  const WHITE_STROKE_PX = 2; // screen-space
  const baseRadius = Math.max(1, zoomAdjustedBrushSize / 2); // local-space
  const innerOutlineRadius = Math.max(0.5, baseRadius - WHITE_STROKE_PX / (2 * zoom));
  const outerOutlineRadius = baseRadius + WHITE_STROKE_PX / (2 * zoom);

  return (
    <Group
      x={displayDimensions.x}
      y={displayDimensions.y}
      width={displayDimensions.width}
      height={displayDimensions.height}
      listening={activeTool === "erase"}
      onMouseLeave={() => {
        lastPreviewPointRef.current = null;
        clearPreview();
        setRingPosition(null);

        if (dragEraseRafRef.current !== null) {
          cancelAnimationFrame(dragEraseRafRef.current);
          dragEraseRafRef.current = null;
        }
      }}
      {...(activeTool === "erase"
        ? {
            onMouseMove: (e: KonvaEventObject<MouseEvent>) => {
              handlePreviewMouseMove(e);
              handleDragErase(e);
            },
            onMouseDown: handleMouseDown,
            onMouseUp: handleMouseUp,
            onTouchStart: handleMouseDown,
            onTouchMove: handleDragErase,
            onTouchEnd: handleMouseUp
          }
        : {})}
    >
      <Rect
        x={0}
        y={0}
        width={displayDimensions.width}
        height={displayDimensions.height}
        fill="rgba(0,0,0,0)"
        listening={activeTool === "erase"}
      />
      {previewCanvasRef.current &&
        previewCanvasRef.current.width > 0 &&
        previewCanvasRef.current.height > 0 && (
          <Image
            ref={previewImageRef}
            image={previewCanvasRef.current as unknown as CanvasImageSource}
            x={0}
            y={0}
            width={displayDimensions.width}
            height={displayDimensions.height}
            listening={false}
          />
        )}
      {shouldShowRing && displayRingPosition && (
        <>
          <Circle
            x={displayRingPosition.x}
            y={displayRingPosition.y}
            radius={innerOutlineRadius}
            stroke="#000"
            strokeWidth={0.5}
            strokeScaleEnabled={false}
            listening={false}
          />
          <Circle
            x={displayRingPosition.x}
            y={displayRingPosition.y}
            radius={baseRadius}
            stroke="#ffffff"
            strokeWidth={WHITE_STROKE_PX}
            strokeScaleEnabled={false}
            listening={false}
          />
          <Circle
            ref={ringRef}
            x={displayRingPosition.x}
            y={displayRingPosition.y}
            radius={outerOutlineRadius}
            stroke="#000"
            strokeWidth={0.5}
            strokeScaleEnabled={false}
            listening={false}
          />
        </>
      )}
    </Group>
  );
}
